import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getSurveyCategories, getSurveyQuestions, submitLectureSurvey, SurveyQuestion, SurveyCategory, getLectureProgress } from '../../services/lectureApi';

interface SurveyProps {}

interface SurveyResponse {
  [key: string]: number | string;
}

const Survey: React.FC<SurveyProps> = () => {
  const { lectureId } = useParams<{ lectureId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [categories, setCategories] = useState<SurveyCategory[]>([]);
  const [responses, setResponses] = useState<SurveyResponse>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completionRate, setCompletionRate] = useState<number | null>(null);
  const [checkingProgress, setCheckingProgress] = useState(true);
  
  // URL에서 강의 제목 가져오기
  const lectureTitle = searchParams.get('lecture_title') ? decodeURIComponent(searchParams.get('lecture_title')!) : '';

  // 수강률 확인
  useEffect(() => {
    const checkProgress = async () => {
      if (!lectureId) {
        setCheckingProgress(false);
        return;
      }

      try {
        const progressData = await getLectureProgress(parseInt(lectureId));
        const rate = progressData.data?.completion_rate || 0;
        // completion_rate가 소수점 형태(0.0104)인지 백분율 형태(1.04)인지 확인
        const completionRatePercent = rate < 1 ? rate * 100 : rate;
        setCompletionRate(completionRatePercent);
        
        // 수강률이 100%가 아니면 alert 표시하고 창 닫기
        if (completionRatePercent < 100) {
          alert(`수강 완료한 강의에 대해서만 설문을 제출할 수 있습니다.\n\n현재 수강률: ${Math.floor(completionRatePercent * 100) / 100}%\n수강률 100%를 달성한 후 다시 시도해 주세요.`);
          // 팝업 창이면 창 닫기, 아니면 페이지 이동
          if (window.opener) {
            window.close();
          } else {
            navigate('/mypage/my-classroom');
          }
        }
      } catch (error) {
        /// console.error('수강률 확인 실패:', error);
        setCompletionRate(0);
        alert('수강률을 확인할 수 없습니다. 다시 시도해 주세요.');
        // 팝업 창이면 창 닫기, 아니면 페이지 이동
        if (window.opener) {
          window.close();
        } else {
          navigate('/mypage/my-classroom');
        }
      } finally {
        setCheckingProgress(false);
      }
    };

    checkProgress();
  }, [lectureId, navigate]);

  // 설문 카테고리와 질문 로드
  useEffect(() => {
    const loadSurveyData = async () => {
      try {
        setLoading(true);
        
        // 카테고리와 질문을 병렬로 로드
        const [categoriesResponse, questionsResponse] = await Promise.all([
          getSurveyCategories(),
          getSurveyQuestions()
        ]);
        
        // 응답 상태 체크
        if ((categoriesResponse.code === 200 || categoriesResponse.code === 1073741824) && 
            (questionsResponse.code === 200 || questionsResponse.code === 1073741824)) {
          
          // active한 카테고리와 질문만 필터링
          const activeCategories = categoriesResponse.data.filter(cat => cat.is_active);
          const activeQuestions = questionsResponse.data.filter(q => q.active);
          
          setCategories(activeCategories);
          setQuestions(activeQuestions);
          
          // 응답 초기값 설정
          const initialResponses: SurveyResponse = {};
          activeQuestions.forEach(question => {
            if (question.selection_count === 0) {
              // 텍스트 입력 질문
              initialResponses[`question_${question.id}`] = '';
            } else {
              // 척도 질문
              initialResponses[`question_${question.id}`] = 0;
            }
          });
          setResponses(initialResponses);
        } else {
          throw new Error('설문 데이터를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        /// console.error('설문 데이터 로드 실패:', error);
        setError('설문 데이터를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadSurveyData();
  }, []);

  const handleRatingChange = (field: string, value: number) => {
    setResponses(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTextChange = (field: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 제출 버튼 활성화 여부 확인 (척도 질문만 체크)
  const isSubmitEnabled = () => {
    const ratingQuestions = questions.filter(q => q.selection_count > 0);
    return ratingQuestions.every(question => 
      responses[`question_${question.id}`] && responses[`question_${question.id}`] !== 0
    );
  };

  const handleSubmit = async () => {
    if (!lectureId) {
      alert('강의 정보가 없습니다.');
      return;
    }

    try {
      // 수강률 확인
      const progressData = await getLectureProgress(parseInt(lectureId));
      const completionRate = progressData.data?.completion_rate || 0;
      
      if (completionRate < 100) {
        alert(`설문 제출에 실패했습니다.\n\n수강 완료한 강의에 대해서만 설문을 제출할 수 있습니다.\n현재 수강률: ${Math.floor(completionRate)}%\n수강률 100%를 달성한 후 다시 시도해주세요.`);
        return;
      }

      const response = await submitLectureSurvey(parseInt(lectureId), responses);
      
      if (response.code === 200 || response.code === 201 || response.code === 1073741824) {
        alert('설문이 제출되었습니다. 소중한 의견 감사합니다!');
        
        // 팝업 창이면 창 닫기, 아니면 페이지 이동
        if (window.opener) {
          window.close();
        } else {
          navigate('/mypage/my-classroom');
        }
      } else {
        throw new Error(response.message || '설문 제출에 실패했습니다.');
      }
    } catch (error: any) {
      /// console.error('설문 제출 실패:', error);
      
      // 서버에서 반환한 에러 메시지 확인
      const errorMessage = error.response?.data?.message || error.message || '알 수 없는 오류';
      
      if (error.response?.status === 401 || error.message === 'Unauthorized') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else if (error.response?.status === 403) {
        // 403 에러의 경우 서버 메시지만 표시 (서버 메시지가 이미 충분히 명확함)
        alert(`설문 제출에 실패했습니다.\n\n${errorMessage}`);
      } else {
        alert(`설문 제출에 실패했습니다: ${errorMessage}`);
      }
    }
  };

  const renderRatingScale = (field: string, currentValue: number) => {
    return (
      <RatingContainer>
        <RatingScale currentValue={currentValue}>
          {[1, 2, 3, 4, 5].map(value => (
            <RatingButton
              key={value}
              active={currentValue === value}
              filled={currentValue >= value}
              onClick={() => handleRatingChange(field, value)}
            />
          ))}
        </RatingScale>
        <RatingLabels>
          <RatingLabel>전혀 그렇지 않다</RatingLabel>
          <RatingLabel>그렇지 않다</RatingLabel>
          <RatingLabel>보통이다</RatingLabel>
          <RatingLabel>그렇다</RatingLabel>
          <RatingLabel>매우 그렇다</RatingLabel>
        </RatingLabels>
      </RatingContainer>
    );
  };

  // 카테고리별로 질문들을 그룹화
  const groupQuestionsByCategory = () => {
    const grouped: { [key: string]: SurveyQuestion[] } = {};
    questions.forEach(question => {
      if (!grouped[question.category_name]) {
        grouped[question.category_name] = [];
      }
      grouped[question.category_name].push(question);
    });
    return grouped;
  };

  if (loading || checkingProgress) {
    return (
      <Container>
        <LoadingMessage>설문 질문을 불러오는 중...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  // 수강률이 100%가 아니면 폼을 표시하지 않음 (alert는 이미 표시됨)
  if (completionRate === null || completionRate < 100) {
    return (
      <Container>
        <LoadingMessage>페이지를 이동하는 중...</LoadingMessage>
      </Container>
    );
  }

  const groupedQuestions = groupQuestionsByCategory();

  return (
    <Container>
      <Header>
        <Title>강의 설문</Title>
        {lectureTitle && <LectureTitle>{lectureTitle}</LectureTitle>}
        {/* <Subtitle>설문에 참여해 주셔서 감사합니다</Subtitle> */}
      </Header>

      <SurveyForm>
        {Object.entries(groupedQuestions).map(([categoryName, categoryQuestions]) => (
          <Section key={categoryName}>
            <SectionTitle>{categoryName}</SectionTitle>
            
            {categoryQuestions.map((question) => (
              <QuestionGroup key={question.id}>
                <Question>{question.question}</Question>
                
                {question.selection_count === 0 ? (
                  // 텍스트 입력 질문
                  <>
                    <TextArea
                      placeholder="내용을 입력해 주세요"
                      value={responses[`question_${question.id}`] as string || ''}
                      onChange={(e) => handleTextChange(`question_${question.id}`, e.target.value)}
                      maxLength={500}
                    />
                    <CharCount>
                      {(responses[`question_${question.id}`] as string || '').length}/500
                    </CharCount>
                  </>
                ) : (
                  // 척도 질문
                  renderRatingScale(
                    `question_${question.id}`, 
                    responses[`question_${question.id}`] as number || 0
                  )
                )}
              </QuestionGroup>
            ))}
          </Section>
        ))}

        <SubmitSection>
          <SubmitNotice>
            본 설문은 강의 및 플랫폼 품질 개선을 위한 목적으로만 활용됩니다.
          </SubmitNotice>
          <SubmitButton 
            onClick={handleSubmit}
            disabled={!isSubmitEnabled()}
          >
            제출하기
          </SubmitButton>
        </SubmitSection>
      </SurveyForm>
    </Container>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px 40px 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  color: #171719;
  font-size: 32px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 40px;
  margin: 0 0 16px 0;
`;

const LectureTitle = styled.h2`
  color: #448181;
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
  margin: 0 0 16px 0;
  word-break: keep-all;
  text-align: center;
`;

const Subtitle = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
`;

const SurveyForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  color: #448181;
  font-size: 24px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
`;

const QuestionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Question = styled.p`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  margin: 0;
`;

const RatingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const RatingLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  position: relative;
`;

const RatingLabel = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  width: 20px;
  position: relative;
  white-space: nowrap;
  transform: translateX(-50%);
  
  &:first-child {
    transform: translateX(0);
    text-align: left;
  }
  
  &:last-child {
    transform: translateX(-100%);
    text-align: right;
  }
`;

const RatingScale = styled.div<{ currentValue: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 0 10px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 20px;
    right: 20px;
    height: 3px;
    background-color: rgba(112, 115, 124, 0.22);
    z-index: 0;
    border-radius: 2px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 20px;
    width: ${props => {
      if (props.currentValue === 0) return '0';
      // 전체 진행바 너비에서 선택된 위치까지의 비율 계산
      const totalWidth = 'calc(100% - 40px)'; // 좌우 패딩 20px씩 제외
      const progress = (props.currentValue - 1) / 4; // 0~1 사이의 값
      return `calc(${totalWidth} * ${progress})`;
    }};
    height: 3px;
    background-color: #448181;
    z-index: 0;
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const RatingButton = styled.button<{ active: boolean; filled: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #448181;
  background-color: ${props => props.filled ? '#448181' : 'white'};
  cursor: pointer;
  z-index: 1;
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.filled ? '#3a6b6b' : '#f0f7f7'};
    border-color: ${props => props.filled ? '#3a6b6b' : '#448181'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 16px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  
  &:focus {
    border-color: #448181;
  }
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const CharCount = styled.div`
  text-align: right;
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
`;

const SubmitSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 32px;
`;

const SubmitNotice = styled.p`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  max-width: 360px;
  padding: 16px 24px;
  background-color: #448181;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: #3a6b6b;
  }
  
  &:disabled {
    background-color: rgba(112, 115, 124, 0.22);
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #ff3b30;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
`;

export default Survey;
